import React, { memo, useState, useMemo, useCallback } from 'react';
import {
    createUseStyles,
    Icon,
    Text,
    Switch,
    Advice,
    RadioGroup,
    Popover,
    Markdown,
    useTheme,
} from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from 'lib/Web5Context';
import { ADVANCED_IMPORT_IDENTIFIERS } from '../../../constants';

import styles from './styles';

const images = {
    0: 'imgImportsName',
    1: 'imgImportsFmId',
    2: 'imgImportsExtId',
};

const useStyles = createUseStyles(styles, 'IdentifierPicker');

const IdentifierPicker = memo(
    ({ setIsAdvanced, isAdvanced, setIdentifier, identifier, withMarginTop, ...props }) => {
        const classes = useStyles();
        const theme = useTheme();
        const { t } = useWeb5Context();
        const [popoverVisible, setPopoverVisible] = useState('');

        const onChangeAdvanced = useCallback(
            (bool) => {
                setIsAdvanced && setIsAdvanced(bool);
            },
            [setIsAdvanced],
        );

        const getPopoverContent = useCallback(
            ({ id, title, text }) => {
                const imgName = images[id];
                let imageSrc = null;

                if (props[imgName]) imageSrc = props[imgName];

                return (
                    <div className={classes.popoverContainer}>
                        {imageSrc && (
                            <img className={classes.popoverImage} src={imageSrc} alt="import" />
                        )}
                        <div className={classes.popoverTextContainer}>
                            <div className={classes.popoverText}>
                                <Text type="h6" bold color={theme.colors.textLight.primary}>
                                    {title}
                                </Text>
                                <Markdown className={classes.popoverTextMarkdown} content={text} />
                            </div>
                        </div>
                    </div>
                );
            },
            [classes, props, theme],
        );

        const popoverDefaultProps = useMemo(() => {
            return {
                className: classes.popover,
                placement: 'top',
                trigger: ['hover'],
            };
        }, [classes]);

        const onMouseOverOrOut = useCallback(
            (id) => () => {
                setPopoverVisible(id || '');
            },
            [],
        );

        const options = useMemo(
            () => [
                {
                    label: (
                        <div className={classes.option}>
                            <Text>{t('label_companyname')}</Text>
                            <Popover
                                {...popoverDefaultProps}
                                content={getPopoverContent({
                                    id: ADVANCED_IMPORT_IDENTIFIERS.name,
                                    title: t('label_companyname'),
                                    text: t('cfm_label_entity_name_desc'),
                                })}
                                visible={popoverVisible === ADVANCED_IMPORT_IDENTIFIERS.name}
                            >
                                <Icon
                                    name="info"
                                    color={theme.colors.grey[500]}
                                    onMouseOver={onMouseOverOrOut(ADVANCED_IMPORT_IDENTIFIERS.name)}
                                    onMouseOut={onMouseOverOrOut()}
                                />
                            </Popover>
                        </div>
                    ),
                    value: ADVANCED_IMPORT_IDENTIFIERS.name,
                    className: 'import-export__import-type--update-id--name',
                },
                {
                    label: (
                        <div className={classes.option}>
                            <Text>{t('cfm_label_id_account')}</Text>
                            <Popover
                                {...popoverDefaultProps}
                                content={getPopoverContent({
                                    id: ADVANCED_IMPORT_IDENTIFIERS.id,
                                    title: t('cfm_label_id_account'),
                                    text: t('cfm_label_fm_id_desc'),
                                })}
                                visible={popoverVisible === ADVANCED_IMPORT_IDENTIFIERS.id}
                            >
                                <Icon
                                    name="info"
                                    color={theme.colors.grey[500]}
                                    onMouseOver={onMouseOverOrOut(ADVANCED_IMPORT_IDENTIFIERS.id)}
                                    onMouseOut={onMouseOverOrOut()}
                                />
                            </Popover>
                        </div>
                    ),
                    value: ADVANCED_IMPORT_IDENTIFIERS.id,
                    className: 'import-export__import-type--update-id--id',
                },
                {
                    label: (
                        <div className={classes.option}>
                            <Text>{t('cfm_label_external_id_account')}</Text>
                            <Popover
                                {...popoverDefaultProps}
                                content={getPopoverContent({
                                    id: ADVANCED_IMPORT_IDENTIFIERS.extId,
                                    title: t('cfm_label_external_id_account'),
                                    text: t('cfm_label_customer_id_desc'),
                                })}
                                visible={popoverVisible === ADVANCED_IMPORT_IDENTIFIERS.extId}
                            >
                                <Icon
                                    name="info"
                                    color={theme.colors.grey[500]}
                                    onMouseOver={onMouseOverOrOut(
                                        ADVANCED_IMPORT_IDENTIFIERS.extId,
                                    )}
                                    onMouseOut={onMouseOverOrOut()}
                                />
                            </Popover>
                        </div>
                    ),
                    value: ADVANCED_IMPORT_IDENTIFIERS.extId,
                    className: 'import-export__import-type--update-id--extid',
                },
            ],
            [
                t,
                classes.option,
                theme.colors,
                getPopoverContent,
                popoverDefaultProps,
                popoverVisible,
                onMouseOverOrOut,
            ],
        );

        const onChangeIdentifier = useCallback(
            (id) => {
                setIdentifier && setIdentifier(id);
            },
            [setIdentifier],
        );

        return (
            <div
                className={classnames(classes.root, { [classes.withMarginTop]: withMarginTop })}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <div className={classes.toggleContainer}>
                    <div className={classes.baseLeft}>
                        <Icon name="relationFields" />
                        <Text
                            type="subtitle"
                            bold={true}
                            color={
                                isAdvanced
                                    ? theme.colors.textLight.primary
                                    : theme.colors.textLight.secondary
                            }
                        >
                            {t('cfm_label_match_accounts')}
                        </Text>
                    </div>
                    <div className={classes.baseLeft}>
                        <span
                            className={classes.switchWrapper}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Switch checked={isAdvanced} onChange={onChangeAdvanced} />
                        </span>
                        <Text
                            color={
                                isAdvanced
                                    ? theme.colors.textLight.primary
                                    : theme.colors.textLight.secondary
                            }
                        >
                            {t('cfm_label_advanced_mode')}
                        </Text>
                    </div>
                </div>
                {isAdvanced && (
                    <div className={classes.advancedContainer}>
                        <Advice type="info" showIcon showCollapse={false}>
                            {t('cfm_label_choose_identifier_desc')}
                        </Advice>
                        <div className={classes.options}>
                            <RadioGroup
                                className={classes.radioGroup}
                                labelMode="vertical"
                                options={options}
                                onChange={onChangeIdentifier}
                                value={identifier}
                                orientation="horizontal"
                                isFullWidth
                                overrides={{
                                    root: { style: { width: '100%', paddingBottom: '0px' } },
                                    radioControl: { style: { marginBottom: '0px' } },
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    },
);

export default IdentifierPicker;
